import React from 'react';
import { Button, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import type { FinancialInstrument, Order, UnderlyingAsset } from 'state';
import { CREATING, FINISHED, FROZEN, PAUSED, selectors } from 'state';
import { formatters } from 'services';
import {
  currentSimplifiedOptionOrders,
  isSimplifiedOptionSettlementFailed,
  simplifiedOptionCurrencySymbol,
  simplifiedOptionSnapshotContracts,
  simplifiedOptionSnapshotActionLabel,
} from '../../../state/app/simplified_options/presentation';

type Row = FinancialInstrument & UnderlyingAsset;

type Props = {
  financialInstruments: FinancialInstrument[];
  selectedInstrumentId?: number;
  selectInstrument?: (instrument: FinancialInstrument) => void;
  showOrderModal?: () => void;
  readOnly?: boolean;
  showDrawer?: boolean;
};

/** Formats nullable option audit money while keeping the native/reporting currency visible. */
const optionMoney = (
  value: number | string | null | undefined,
  currency: string,
) => (value == null ? '-' : `${formatters.commas2Digits(value)} ${currency}`);

/** Renders class-19 aggregate economics and the exact active-position order drawer. */
const SimplifiedOptionInstrumentTable: React.FC<Props> = ({
  financialInstruments,
  selectInstrument,
  showOrderModal,
  readOnly = false,
  showDrawer = true,
}) => {
  const assets = useSelector(selectors.underlyingAssets);
  const currencies = useSelector(selectors.currencies);
  const session = useSelector(selectors.activeSession);
  const allOrders = useSelector(selectors.orders);
  const reportingCurrency = currencies[session.reporting_currency];
  const tradingDisabled = [PAUSED, CREATING, FINISHED, FROZEN].includes(
    session.status,
  );
  const instrumentIds = financialInstruments.map(item => item.id);
  const orders = allOrders.filter(order =>
    instrumentIds.includes(order.financial_instrument),
  );
  const dataSource: Row[] = financialInstruments.map(instrument => ({
    ...assets[instrument.underlying_asset],
    ...instrument,
    id: instrument.id,
  }));

  const drawerColumns: ColumnsType<Order> = [
    {
      title: 'Action',
      render: (_, order) =>
        simplifiedOptionSnapshotActionLabel(
          order.transaction,
          order.simplified_option_snapshot?.execution_effect || '',
        ),
    },
    {
      title: 'Contracts',
      align: 'right',
      render: (_, order) =>
        formatters.commasNoDigits(
          simplifiedOptionSnapshotContracts(order.simplified_option_snapshot),
        ),
    },
    {
      title: 'Premium',
      align: 'right',
      render: (_, order) => {
        const snapshot = order.simplified_option_snapshot;
        const symbol = simplifiedOptionCurrencySymbol(
          snapshot?.native_currency,
          currencies,
        );
        return optionMoney(snapshot?.signed_total_premium, symbol);
      },
    },
    {
      title: 'Registered expiry',
      render: (_, order) => {
        const snapshot = order.simplified_option_snapshot;
        return snapshot ? `${snapshot.days_to_expiry} days` : '-';
      },
    },
    {
      title: 'Payoff',
      align: 'right',
      render: (_, order) => {
        const snapshot = order.simplified_option_snapshot;
        const symbol = simplifiedOptionCurrencySymbol(
          snapshot?.native_currency,
          currencies,
        );
        return optionMoney(snapshot?.display_payoff, symbol);
      },
    },
  ];

  const columns: ColumnsType<Row> = [
    {
      title: 'Instrument',
      render: (_, record) => record.name || record.ticker,
    },
    {
      title: 'Position',
      render: (_, record) => {
        const contracts = record.simplified_option?.contracts || 0;
        const settlementFailed = isSimplifiedOptionSettlementFailed(
          record.simplified_option,
        );
        const direction =
          contracts > 0 ? 'Long' : contracts < 0 ? 'Short' : 'Square';
        return (
          <span>
            <Tag
              color={
                contracts > 0 ? 'green' : contracts < 0 ? 'red' : undefined
              }
            >
              {direction}
            </Tag>
            {formatters.commasNoDigits(Math.abs(contracts))} contracts
            {settlementFailed && (
              <Tag color="error" style={{ marginLeft: 8 }}>
                Settlement failed
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: 'Current strike',
      align: 'right',
      render: (_, record) => {
        const strike = record.simplified_option?.current_strike;
        return strike == null ? '-' : formatters.commas2Digits(strike);
      },
    },
    {
      title: 'Annualized IV',
      align: 'right',
      render: (_, record) => {
        // Bond rows show supplied BP instead; volatility remains an economic input only.
        if (record.underlying_kind === 'BOND') return null;
        const volatility =
          record.simplified_option?.current_annualized_implied_volatility;
        return volatility == null ? '-' : formatters.percent2Digit(volatility);
      },
    },
    {
      title: 'Bp',
      align: 'right',
      render: (_, record) => {
        // Display the period-frozen bond BP while keeping equity cells truly empty.
        if (record.underlying_kind !== 'BOND') return null;
        const basisPoints = record.simplified_option?.current_basis_points;
        return basisPoints == null
          ? '-'
          : formatters.commas2Digits(basisPoints);
      },
    },
    {
      title: 'Expiry',
      render: (_, record) => {
        const option = record.simplified_option;
        return option?.days_to_expiry == null
          ? '-'
          : `${option.days_to_expiry} days`;
      },
    },
    {
      title: 'Currency',
      render: (_, record) =>
        simplifiedOptionCurrencySymbol(
          record.simplified_option?.native_currency || record.variable_currency,
          currencies,
        ) || '-',
    },
    {
      title: `Notional (${reportingCurrency?.symbol || ''})`,
      align: 'right',
      render: (_, record) =>
        formatters.commasNoDigits(
          record.simplified_option?.reporting_controlled_exposure ??
            record.simplified_option?.controlled_exposure ??
            0,
        ),
    },
    {
      title: 'Cumulative net premiums',
      align: 'right',
      render: (_, record) => {
        const option = record.simplified_option;
        const hasReporting = option?.reporting_premium != null;
        const currency = hasReporting
          ? reportingCurrency?.symbol || ''
          : simplifiedOptionCurrencySymbol(
              option?.native_currency || record.variable_currency,
              currencies,
            );
        return optionMoney(
          hasReporting
            ? option?.reporting_premium
            : (option?.native_premium ?? option?.premium),
          currency,
        );
      },
    },
    {
      title: `Unrealized payoff (${reportingCurrency?.symbol || ''})`,
      dataIndex: 'reporting_unrealized_pnl',
      align: 'right',
      render: formatters.commas2Digits,
    },
    {
      title: `Realized P&L (${reportingCurrency?.symbol || ''})`,
      dataIndex: 'reporting_realized_pnl',
      align: 'right',
      render: formatters.commas2Digits,
    },
    ...(!readOnly
      ? [
          {
            title: 'Actions',
            fixed: 'right' as const,
            render: (_: unknown, record: Row) => (
              <Button
                type="link"
                disabled={
                  tradingDisabled ||
                  isSimplifiedOptionSettlementFailed(record.simplified_option)
                }
                onClick={() => {
                  selectInstrument?.(
                    financialInstruments.find(item => item.id === record.id) ||
                      financialInstruments[0],
                  );
                  showOrderModal?.();
                }}
              >
                Trade
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      rowKey="id"
      bordered
      pagination={false}
      scroll={{ x: true }}
      dataSource={dataSource}
      columns={columns}
      expandable={
        showDrawer
          ? {
              rowExpandable: record =>
                currentSimplifiedOptionOrders(
                  orders,
                  record.simplified_option?.active_position_id ?? null,
                ).length > 0,
              expandedRowRender: record => (
                // The wrapper avoids Ant's direct-child negative margins, which
                // conflict with PM's compact table padding and clip both edges.
                <div className="simplified-option-legs-drawer">
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={drawerColumns}
                    dataSource={currentSimplifiedOptionOrders(
                      orders,
                      record.simplified_option?.active_position_id ?? null,
                    )}
                  />
                </div>
              ),
            }
          : undefined
      }
    />
  );
};

export default SimplifiedOptionInstrumentTable;
